import type { ContentPage } from "@/lib/content/types";

// CRM › Sales Performance hub.
export const salesPerformance: ContentPage = {
  slug: "sales-performance",
  template: "hrmsFeature",
  seo: {
    title: "Sales Performance Software - Forecast & Analyze Sales | GetBiz CRM",
    description: "Boost sales performance with GetBiz CRM — accurate forecasting, multi-currency selling, performance analytics and visitor tracking in one place.",
    keywords: ["sales performance", "sales forecasting software", "sales analytics", "multi currency crm", "visitor tracking", "GetBiz CRM"],
    ogImage: "/assets/data/seoimages/sales-performance.webp",
  },
  leaf: {
    hub: true,
    benefits: [
      { t: "Accurate forecasts", d: "Predict revenue from your live pipeline." },
      { t: "Sell globally", d: "Quote and close in any currency." },
      { t: "Know what works", d: "Analytics across reps, teams and regions." },
      { t: "Warm outreach", d: "Spot the companies visiting your site." },
      { t: "Hit targets", d: "Track performance against goals in real time." },
      { t: "Leadership clarity", d: "A clear view of what will close." },
      { t: "Live exchange rates", d: "Multi-currency stays accurate automatically." },
      { t: "Act on intent", d: "Turn anonymous traffic into timely outreach." },
    ],
    process: {
      title: "How Sales Performance Works, Step by Step",
      description: "Measure, forecast and lift sales performance from one connected view.",
      steps: [
        { title: "Track Pipeline", caption: "Every deal and stage flows into the CRM.", image: "/assets/images/img/product/crm/track-pipeline.webp" },
        { title: "Forecast Revenue", caption: "Build data-driven forecasts from live pipeline.", image: "/assets/images/img/product/crm/forecast-revenue.webp" },
        { title: "Analyze Results", caption: "See what drives revenue by rep and region.", image: "/assets/images/img/product/crm/analyze-results.webp" },
        { title: "Spot Visitors", caption: "Identify companies browsing your site.", image: "/assets/images/img/product/crm/spot-visitors.webp" },
        { title: "Act & Coach", caption: "Turn insight into timely outreach and coaching.", image: "/assets/images/img/product/crm/act-coach.webp" },
      ],
    },
    highlights: {
      title: "Key Highlights of Sales Performance",
      items: [
        { image: "/assets/images/img/product/crm/forcasting-confidance.webp", title: "Forecast with confidence.", href: "/crm-software/sales-performance/forecasting/", description: "Build data-driven forecasts from your live pipeline so leadership always has an accurate view of what will close.", checklist: ["Pipeline-based","Data-driven","Leadership view","Accurate"] },
        { image: "/assets/images/img/product/crm/drives-revenue.webp", title: "Know what drives revenue.", href: "/crm-software/sales-performance/analytics/", description: "Dig into performance across reps, products and regions to understand the deals and behaviors that drive revenue.", checklist: ["By rep/region","Drill down","Find drivers","Real-time"] },
        { image: "/assets/images/img/product/crm/trafic-leads.webp", title: "Turn traffic into leads.", href: "/crm-software/sales-performance/visitor-tracking/", description: "Identify the companies browsing your site, see what they view, and turn anonymous traffic into warm outreach.", checklist: ["Identify companies","See views","Warm leads","Timely outreach"] },
      ],
    },
    hero: {
      caption: "Sales Performance",
      title: "Measure, Forecast & Boost Sales Performance",
      description: "Forecast revenue, sell in any currency, analyze results and track website visitors — GetBiz CRM gives you everything to lift sales performance.",
      h1: "caption",
    },
    split: {
      title: "Everything You Need for Sales Performance",
      bullets: [
        { title: "Accurate Forecasting", description: "Predict revenue with pipeline-based, data-driven forecasts." },
        { title: "Sell Globally", description: "Quote and close in multiple currencies with live rates." },
        { title: "Performance Analytics", description: "See what drives results across reps, teams and regions." },
        { title: "Visitor Intelligence", description: "Know which companies visit your site and act on it." },
      ],
      image: "/assets/images/img/product/crm/sales-performance.webp",
      imageAlt: "Sales Performance — GetBiz CRM",
    },
    cardsHeading: {
      title: "Key Features of Sales Performance",
      description: "Explore everything Sales Performance brings to your GetBiz CRM.",
    },
    cards: [
      { title: "Forecasting", href: "/crm-software/sales-performance/forecasting/", description: "Build data-driven forecasts from your live pipeline, so leadership always has an accurate view of what will close.", image: "/assets/images/img/product/crm/forecasting.webp", imageAlt: "GetBiz CRM forecasting", checklist: ["Pipeline-Based","Data-Driven","Leadership View","Accurate"] },
      { title: "Multiple Currencies", href: "/crm-software/sales-performance/multiple-currencies/", description: "Quote, close and report across currencies with live exchange rates.", image: "/assets/images/img/product/crm/multiple-currencies.webp", imageAlt: "GetBiz CRM multiple currencies", checklist: ["Any Currency","Live Rates","Quote & Close","Global Reports"] },
      { title: "Analytics", href: "/crm-software/sales-performance/analytics/", description: "Dig into performance across reps, products and regions to understand the behaviors and deals that drive revenue.", image: "/assets/images/img/product/crm/analytics-sub.webp", imageAlt: "GetBiz CRM analytics", checklist: ["By Rep/Region","Drill Down","Find Drivers","Real-Time"] },
      { title: "Visitor Tracking", href: "/crm-software/sales-performance/visitor-tracking/", description: "Identify the companies browsing your site, see what they view, and turn anonymous traffic into warm, timely outreach.", image: "/assets/images/img/product/crm/visitor-tracking.webp", imageAlt: "GetBiz CRM visitor tracking", checklist: ["Identify Companies","See Views","Warm Leads","Timely Outreach"] },
    ],
    testimonials: {
      title: "Businesses across India love & trust GetBiz!",
      description: "See why teams choose GetBiz CRM to run their sales.",
    },
    bookDemoTitle: "Ready to get more from sales performance?",
    relatedBlogs: "static",
    metaKey: "sales-performance",
  },
  sections: [],
};
